import {
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { FixedSizeGrid, GridChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import {
  Progress,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { defaultDimensions, useOnResize } from 'src/hooks/useOnResize';
import { useRequiredContext } from 'src/hooks/useRequiredContext';

import classes from './Grid.module.scss';
import { offset } from './consts';

export interface GridItemProps<T>
  extends Omit<GridChildComponentProps<T>, 'columnIndex' | 'rowIndex'> {
  index: number;
}

interface GridContextValue {
  Item: FC<{ data: any }>;
  columnCount: number;
}

const GridContext = createContext<GridContextValue | undefined>(undefined);

export const Cell = <T,>({
  columnIndex,
  rowIndex,
  style,
  data,
}: GridChildComponentProps<T[]>): ReactElement => {
  const { columnCount, Item } = useRequiredContext(GridContext);
  const index = rowIndex * columnCount + columnIndex;
  const datum = data[index];

  return (
    <div style={style}>
      <div
        style={{
          marginLeft: !columnIndex ? 0 : offset / 2,
          marginRight: columnIndex === columnCount - 1 ? 0 : offset / 2,
          marginBottom: offset,
        }}
      >
        {datum ? <Item data={datum} /> : null}
      </div>
    </div>
  );
};

export interface GridProps<T> {
  rows: T[];
  loadMore: (startIndex: number, stopIndex: number) => Promise<void>;
  rowHeight: number;
  Item: FC<{ data: T }>;
  noResults: ReactNode;
  loading: ReactNode;
  isLoading: boolean;
  columnCount: number;
}

interface NoResultsProps {
  children: ReactNode;
}

const NoResults: FC<NoResultsProps> = ({ children }) => {
  return (
    <div className={classes.noResults} data-testid='noResults'>
      <Typography>{children}</Typography>
    </div>
  );
};

interface LoadingProps {
  children: ReactNode;
}

const Loading: FC<LoadingProps> = ({ children }) => {
  return (
    <div className={classes.loading} data-testid='loading'>
      <Typography>{children}</Typography>
      <Progress type='solo' cycled data-testid='floorMapLoadingProgress' />
    </div>
  );
};

interface InnerProps<T> extends GridProps<T> {
  width: number;
  height: number;
}

const Inner = <T,>({
  rows,
  loadMore,
  width,
  height,
  rowHeight,
  columnCount,
  Item,
  noResults,
  isLoading,
  loading,
}: InnerProps<T>): ReactElement => {
  const isItemLoaded = useCallback((index: number) => !!rows[index], [rows]);
  const [scrollWidth, setScrollWidth] = useState(0);
  const columnWidth = (width - scrollWidth) / columnCount;
  const rowCount = Math.ceil(rows.length / columnCount);

  const contextValue = useMemo(
    (): GridContextValue => ({
      columnCount,
      Item,
    }),
    [Item, columnCount],
  );

  const observeScroll = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;

      setScrollWidth(width - node.clientWidth);
    },
    [width],
  );

  if (rows.length) {
    return (
      <GridContext.Provider value={contextValue}>
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={Infinity}
          loadMoreItems={loadMore}
        >
          {({ onItemsRendered, ref }) => (
            <FixedSizeGrid
              className={classes.wrapper}
              ref={ref}
              outerRef={observeScroll}
              itemData={rows}
              rowCount={rowCount}
              columnCount={columnCount}
              columnWidth={columnWidth}
              width={width}
              height={height}
              rowHeight={rowHeight + offset}
              onItemsRendered={({
                overscanRowStartIndex,
                overscanRowStopIndex,
                visibleRowStartIndex,
                visibleRowStopIndex,
              }) => {
                onItemsRendered({
                  overscanStartIndex: overscanRowStartIndex * columnCount,
                  overscanStopIndex: overscanRowStopIndex * columnCount,
                  visibleStartIndex: visibleRowStartIndex * columnCount,
                  visibleStopIndex: visibleRowStopIndex * columnCount,
                });
              }}
            >
              {Cell}
            </FixedSizeGrid>
          )}
        </InfiniteLoader>
      </GridContext.Provider>
    );
  }

  if (isLoading) return <Loading>{loading}</Loading>;

  return <NoResults>{noResults}</NoResults>;
};

export const Grid = <T,>(props: GridProps<T>): ReactElement => {
  const [dimensions, setDimensions] = useState(defaultDimensions);

  const { observe } = useOnResize(setDimensions);

  return (
    <div
      className={classes.container}
      ref={observe}
      data-testid='gridContainer'
    >
      <Inner width={dimensions.width} height={dimensions.height} {...props} />
    </div>
  );
};
